/**
 * Вкладки как примитив.
 *
 * До этого в продукте жили две самодельные реализации, и одна из них не имела
 * ни стрелок, ни `tabpanel` (`AgrocycleScreen.tsx:108-125`): человек с
 * клавиатурой мог дойти до вкладки, но не мог перейти между ними, а
 * озвучиватель не сообщал, что содержимое ниже — это панель выбранной вкладки.
 *
 * Что здесь удерживается формой, а не договорённостью:
 *
 * * **стрелки, Home/End и связка `tab ↔ tabpanel`** принадлежат React Aria;
 * * **выбранная вкладка отличается не только цветом** — вес и подчёркивание;
 * * **`TabList` требует имя.** Экран с двумя списками вкладок без имён — это
 *   два одинаковых «Вкладки» в списке ориентиров.
 *
 * Чего вкладки делать не должны: прятать обязательный шаг работы. Вкладка —
 * это ВИД на одни и те же данные, а не следующий шаг: шаг, спрятанный за
 * вкладкой, человек не находит вовсе. И вкладка — не адрес: когда вид нужно
 * передать ссылкой, он живёт в URL, а не здесь.
 */

import type { ReactNode } from "react";
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from "react-aria-components";
import styles from "./controls.module.css";

/**
 * Ключ вкладки. Совпадает с ключом коллекции React Aria (`string | number`) и
 * намеренно уже, чем `React.Key`: `bigint` ключом коллекции быть не может.
 */
export type TabKey = string | number;

export function Tabs({
  children,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  className,
}: {
  children: ReactNode;
  selectedKey?: TabKey;
  defaultSelectedKey?: TabKey;
  onSelectionChange?: (key: TabKey) => void;
  className?: string;
}) {
  return (
    <AriaTabs
      className={`${styles.tabs} ${className ?? ""}`}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onSelectionChange}
    >
      {children}
    </AriaTabs>
  );
}

export function TabList({
  children,
  label,
}: {
  children: ReactNode;
  /** Чем этот набор вкладок отличается от соседнего. Обязателен. */
  label: string;
}) {
  return (
    <AriaTabList className={styles.tabList} aria-label={label}>
      {children}
    </AriaTabList>
  );
}

export function Tab({
  id,
  children,
  isDisabled,
}: {
  id: TabKey;
  children: ReactNode;
  isDisabled?: boolean;
}) {
  return (
    <AriaTab id={id} className={styles.tab} isDisabled={isDisabled}>
      {children}
    </AriaTab>
  );
}

export function TabPanel({ id, children }: { id: TabKey; children: ReactNode }) {
  return (
    <AriaTabPanel id={id} className={styles.tabPanel}>
      {children}
    </AriaTabPanel>
  );
}
